import React, { useEffect, useRef, useState } from 'react';
import './AdvancedCriteriaPopup.css';
import { ReactComponent as ExpandIcon } from '../../../assets/icons/arrow-down.svg';
import { Criteria } from '../../../../types/screener.type';
import ScreenerCriteriaSelect from '../ScreenerCriteriaSelect/ScreenerCriteriaSelect';
import { AC_GROUPS } from '../../../../constants/screener/AC.types.constant';
import CriteriaDetailList from '../CriteriaDetailList/CriteriaDetailList';
import { Box, Button, Paper } from '@mui/material';

export interface AdvancedCriteriaItem {
  label: string;
  value: string;
  selected: boolean;
}

export interface AdvancedCriteriaPopupProps {
  region: string;
  sector: string;
  tempCriteria: Criteria[];
  setTempCriteria: any;
  setOpenAdvancedCriteria: (openAdvancedCriteria: boolean) => void;
  onGetResults: () => void;
  onCancel: () => void;
  token: string;
}

const AdvancedCriteriaPopup = ({
  region,
  sector,
  tempCriteria,
  setTempCriteria,
  setOpenAdvancedCriteria,
  onGetResults,
  onCancel,
  token,
}: AdvancedCriteriaPopupProps) => {
  const handleClearAll = () => {
    const latestData = tempCriteria.map((item) => ({
      ...item,
      selected: false,
      pendingMinMax: false,
    }));
    setTempCriteria(latestData);
  };

  // Check if any criteria are still loading min/max data
  const hasPendingMinMax = tempCriteria.some(
    (item: Criteria) => item.pendingMinMax === true,
  );

  return (
    <Box
      sx={{
        boxSizing: 'border-box',
        // Centred on the screener container, not the viewport. Capped at 100%
        // of it: anything larger gets clipped by a scrollable ancestor.
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 'min(900px, 100%)',
        height: 'min(900px, 100%)',
        boxShadow: '0 0 12px 0 var(--box-shadow-primary)',
        backgroundColor: 'var(--surface-primary)',
        outline: 'none',
        overflow: 'hidden',
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        border: '1px solid var(--border-primary)',
        borderRadius: '4px',
      }}
    >
      <Box
        sx={{
          boxSizing: 'border-box',
          padding: '16px 24px 0px 24px',
          width: '100%',
          flex: 1,
          display: 'flex',
          flexDirection: 'row',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            boxSizing: 'border-box',
            width: '50%',
            height: '100%',
            overflowY: 'auto',
            paddingRight: '24px',
            borderRight: '1px solid #BDBDBD',
            scrollbarWidth: 'thin',
          }}
        >
          <ScreenerCriteriaSelect
            criteria={tempCriteria}
            setCriteria={setTempCriteria}
            groups={AC_GROUPS}
          />
        </Box>
        <Box
          sx={{
            boxSizing: 'border-box',
            width: '50%',
            height: '100%',
            overflowX: 'hidden',
            overflowY: 'hidden',
            paddingLeft: '24px',
            paddingRight: '12px',
            scrollbarWidth: 'thin',
            '&:hover, &:active, &:focus': {
              overflowY: 'auto',
            },
          }}
        >
          <CriteriaDetailList
            region={region}
            sector={sector}
            tempCriteria={tempCriteria}
            selectedCriteria={tempCriteria.filter((item) => item.selected)}
            setCriteria={setTempCriteria}
            token={token}
          />
        </Box>
      </Box>

      <Box
        sx={{
          boxSizing: 'border-box',
          width: '100%',
          display: 'flex',
          flexDirection: 'row',
          padding: '24px',
          gap: '8px',
          backgroundColor: '#FFFFFF',
          borderTop: '1px solid #E0E0E0',
        }}
      >
        <Button
          className="screener-advanced-criteria-popup-button"
          variant="contained"
          color="primary"
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          className="screener-advanced-criteria-popup-button"
          variant="contained"
          color="primary"
          onClick={handleClearAll}
        >
          Reset All
        </Button>
        <Button
          className="screener-advanced-criteria-popup-button screener-advanced-criteria-popup-button-get-results"
          variant="contained"
          color="primary"
          disabled={hasPendingMinMax}
          onClick={onGetResults}
        >
          Get Results
        </Button>
      </Box>
    </Box>
  );
};

export default AdvancedCriteriaPopup;
